import { Tables } from "../../database.types";
import { isNumber } from "lodash";

export const getRoundTitleText = (
  currentRoundIdx: number | "bonus" | "overtime" | null,
  category?: string,
) => {
  if (currentRoundIdx === "bonus") {
    return "Bonus Round";
  }
  if (currentRoundIdx === "overtime") {
    return "Overtime";
  }
  if (currentRoundIdx === null) {
    return "Game not started";
  }
  return `Round ${currentRoundIdx + 1}: ${category ?? ""}`;
};

export const getErrorMessage = (err: unknown): string => {
  const error = err as any;
  if (error?.message && typeof error.message === "string") {
    return error.message;
  }
  return "Unknown Error";
};

export const isRoundConfigCompleteDB = (
  round: Tables<"rounds">,
  questions: Tables<"questions">[],
): boolean => {
  const q1 = questions.find((q) => q.question_number === 1);
  const q2 = questions.find((q) => q.question_number === 2);
  return !!(
    (round.category || round.round_number === 10) &&
    q1?.question_text &&
    q1?.answer_text &&
    q2?.question_text &&
    q2?.answer_text
  );
};

export const getGameConfigErrors = (
  game: Tables<"games">,
  rounds: Tables<"rounds">[],
  questions: Tables<"questions">[],
  bonusRound: Tables<"bonus_rounds"> | null,
  classicAnswers: Tables<"classic_bonus_answers">[],
  closestGuessQuestions: Tables<"closest_guess_questions">[],
): string[] => {
  const errors: string[] = [];

  if (!game.team_one_id) errors.push("No team 1 selected");
  if (!game.team_two_id) errors.push("No team 2 selected");
  if (!game.team_1_niche_category) errors.push("No niche for team 1");
  if (!game.team_2_niche_category) errors.push("No niche for team 2");

  rounds.forEach((round) => {
    const roundQuestions = questions.filter((q) => q.round_id === round.id);
    if (!isRoundConfigCompleteDB(round, roundQuestions)) {
      errors.push(`Round ${round.round_number} is not complete`);
    }
  });

  if (game.bonus_round_type === "Classic (List)") {
    if (!bonusRound?.question_text) {
      errors.push("Bonus round question is missing");
    }
    const filledAnswers = classicAnswers.filter((a) => a.answer_text);
    if (filledAnswers.length < 25) {
      errors.push("Not enough classic bonus round answers (need 25+)");
    }
  } else {
    const filledQuestions = closestGuessQuestions.filter(
      (q) => q.question_text && q.correct_answer !== null,
    );
    if (filledQuestions.length < 6) {
      errors.push("Not enough closest guess bonus round questions (need 6+)");
    }
  }

  return errors;
};

export const isLifelineUsedDB = (
  lifelines: Tables<"round_lifelines">[],
  teamId: string,
  lifelineType: string,
): boolean => {
  return lifelines.some(
    (l) => l.team_id === teamId && l.lifeline_type === lifelineType,
  );
};

export const isDoubleSipActiveDB = (
  currentRoundLifelines: Tables<"round_lifelines">[],
  teamId: string,
): boolean => {
  return currentRoundLifelines.some(
    (l) => l.team_id === teamId && l.lifeline_type === "Double Sip",
  );
};

export const isDoubleUpActiveDB = (
  currentRoundLifelines: Tables<"round_lifelines">[],
  teamId: string,
): boolean => {
  return currentRoundLifelines.some(
    (l) => l.team_id === teamId && l.lifeline_type === "Double up",
  );
};

export const isCallAFriendActiveDB = (
  currentRoundLifelines: Tables<"round_lifelines">[],
  teamId: string,
): boolean => {
  return currentRoundLifelines.some(
    (l) => l.team_id === teamId && l.lifeline_type === "Phone a Friend",
  );
};

export const getClosestGuessBonusRoundScoreDB = (
  cgQuestions: Tables<"closest_guess_questions">[],
): { team1Score: number; team2Score: number } => {
  let team1Score = 0;
  let team2Score = 0;

  cgQuestions.forEach((q) => {
    if (
      isNumber(q.team_1_answer) &&
      q.team_1_answer > 0 &&
      isNumber(q.team_2_answer) &&
      q.team_2_answer > 0 &&
      isNumber(q.correct_answer)
    ) {
      const team1Difference = Math.abs(q.correct_answer - q.team_1_answer);
      const team2Difference = Math.abs(q.correct_answer - q.team_2_answer);
      if (team1Difference < team2Difference) {
        team1Score += team1Difference === 0 ? 2 : 1;
      }
      if (team2Difference < team1Difference) {
        team2Score += team2Difference === 0 ? 2 : 1;
      }
    }
  });
  return { team1Score, team2Score };
};

export const getClosestGuessBonusRoundPointsDB = (
  cgQuestions: Tables<"closest_guess_questions">[],
): { team1: number; team2: number } => {
  const { team1Score, team2Score } =
    getClosestGuessBonusRoundScoreDB(cgQuestions);
  if (team1Score > 2 && team1Score > team2Score) {
    return { team1: 2, team2: 0 };
  }
  if (team2Score > 2 && team2Score > team1Score) {
    return { team1: 0, team2: 2 };
  }
  return { team1: 0, team2: 0 };
};

export const getClassicBonusRoundPointsDB = (
  classicAnswers: Tables<"classic_bonus_answers">[],
  teamResponses: Tables<"classic_bonus_team_responses">[],
  team1Id: string,
  team2Id: string,
): { team1: number; team2: number } => {
  const team1ResponseCount = teamResponses.filter(
    (r) => r.team_id === team1Id,
  ).length;
  const team2ResponseCount = teamResponses.filter(
    (r) => r.team_id === team2Id,
  ).length;

  if (!(team1ResponseCount >= 5 || team2ResponseCount >= 5)) {
    return { team1: 0, team2: 0 };
  }

  let team1Correct = 0;
  let team2Correct = 0;
  classicAnswers.forEach((a) => {
    if (a.correct_team_id === team1Id) team1Correct++;
    if (a.correct_team_id === team2Id) team2Correct++;
  });

  if (team1Correct > team2Correct) return { team1: 2, team2: 0 };
  if (team2Correct > team1Correct) return { team1: 0, team2: 2 };
  return { team1: 0, team2: 0 };
};

export const getBonusRoundPointsDB = (
  bonusRound: Tables<"bonus_rounds">,
  classicAnswers: Tables<"classic_bonus_answers">[],
  teamResponses: Tables<"classic_bonus_team_responses">[],
  cgQuestions: Tables<"closest_guess_questions">[],
  team1Id: string,
  team2Id: string,
): { team1: number; team2: number } => {
  if (bonusRound.type === "Classic (List)") {
    return getClassicBonusRoundPointsDB(
      classicAnswers,
      teamResponses,
      team1Id,
      team2Id,
    );
  } else if (bonusRound.type === "Closest Guess Battle") {
    return getClosestGuessBonusRoundPointsDB(cgQuestions);
  }
  return { team1: 0, team2: 0 };
};

export const getTeamScoresDB = (
  rounds: Tables<"rounds">[],
  questions: Tables<"questions">[],
  lifelines: Tables<"round_lifelines">[],
  bonusRounds: Tables<"bonus_rounds">[],
  classicBonusAnswers: Tables<"classic_bonus_answers">[],
  classicBonusTeamResponses: Tables<"classic_bonus_team_responses">[],
  closestGuessQuestions: Tables<"closest_guess_questions">[],
  team1Id: string,
  team2Id: string,
): { team1: number; team2: number } => {
  let team1Score = 0;
  let team2Score = 0;

  const isWorthDouble = (
    roundLifelines: Tables<"round_lifelines">[],
    teamId: string,
    roundNumber: number,
  ) =>
    roundLifelines.some(
      (l) => l.team_id === teamId && l.lifeline_type === "Double up",
    ) || roundNumber === 10;

  // Standard round scoring
  const sortedRounds = [...rounds].sort(
    (a, b) => a.round_number - b.round_number,
  );
  sortedRounds.forEach((round) => {
    const roundQuestions = questions.filter((q) => q.round_id === round.id);
    const roundLifelines = lifelines.filter((l) => l.round_id === round.id);

    const q1 = roundQuestions.find((q) => q.question_number === 1);
    const q2 = roundQuestions.find((q) => q.question_number === 2);

    // Question 1 (team 1's question)
    if (q1) {
      const pointValue = isWorthDouble(
        roundLifelines,
        team1Id,
        round.round_number,
      )
        ? 2
        : 1;
      if (q1.correct_team_id === team1Id) {
        team1Score += pointValue;
      }
      if (q1.correct_team_id === team2Id) {
        // Steal - still worth double if team 1 used double up
        team2Score += isWorthDouble(roundLifelines, team1Id, round.round_number)
          ? 2
          : 1;
      }
    }

    // Question 2 (team 2's question)
    if (q2) {
      const pointValue = isWorthDouble(
        roundLifelines,
        team2Id,
        round.round_number,
      )
        ? 2
        : 1;
      if (q2.correct_team_id === team2Id) {
        team2Score += pointValue;
      }
      if (q2.correct_team_id === team1Id) {
        // Steal
        team1Score += pointValue;
      }
    }
  });

  // Bonus round scoring
  const mainBonus = bonusRounds.find((br) => br.phase === "bonus");
  if (mainBonus) {
    const bonusClassicAnswers = classicBonusAnswers.filter(
      (a) => a.bonus_round_id === mainBonus.id,
    );
    const bonusTeamResponses = classicBonusTeamResponses.filter(
      (r) => r.bonus_round_id === mainBonus.id,
    );
    const bonusCGQuestions = closestGuessQuestions.filter(
      (q) => q.bonus_round_id === mainBonus.id,
    );
    const bonusPoints = getBonusRoundPointsDB(
      mainBonus,
      bonusClassicAnswers,
      bonusTeamResponses,
      bonusCGQuestions,
      team1Id,
      team2Id,
    );
    team1Score += bonusPoints.team1;
    team2Score += bonusPoints.team2;
  }

  // Overtime scoring
  const overtime = bonusRounds.find((br) => br.phase === "overtime");
  if (overtime) {
    const otClassicAnswers = classicBonusAnswers.filter(
      (a) => a.bonus_round_id === overtime.id,
    );
    const otTeamResponses = classicBonusTeamResponses.filter(
      (r) => r.bonus_round_id === overtime.id,
    );
    const otCGQuestions = closestGuessQuestions.filter(
      (q) => q.bonus_round_id === overtime.id,
    );
    const otPoints = getBonusRoundPointsDB(
      overtime,
      otClassicAnswers,
      otTeamResponses,
      otCGQuestions,
      team1Id,
      team2Id,
    );
    team1Score += otPoints.team1;
    team2Score += otPoints.team2;
  }

  return { team1: team1Score, team2: team2Score };
};
